import React, { Component } from 'react';
import {
  CardState,
  DropFromSlotsToHomePayload,
  DropFromSlotsToSlotsPayload,
  ICard,
  ICardsRenderInfo,
  LaidSlotsDropPayload,
  SuperMove,
} from '../../redux/types';
import { Group, Rect } from 'react-konva';
import Card from '../Card';
import { getGameX, getHomeX, getSlotY, haveIntersection, hintMove, intersectionXDistance, moveTo } from '../../utils';
import { getDropElementPosition, IDropElement } from '../../functions/hints';
import { Vector2d } from 'konva/types/types';
import global from '../../global';
import _isEqual from 'lodash/isEqual';

type Props = {
  index: number;
  cardParameters: ICardsRenderInfo;
  card: ICard;
  x: number;
  y: number;
  superMove: SuperMove | false;
  dropElements: IDropElement[];
  size: number;
  actions: {
    dropFromSlotsToLaid: (params: LaidSlotsDropPayload) => void;
    dropFromSlotsToSlots: (params: DropFromSlotsToSlotsPayload) => void;
    dropFromSlotsToHome: (params: DropFromSlotsToHomePayload) => void;
    setListening: (listening: boolean) => void;
  };
};

class SlotsCard extends Component<Props> {
  node: any = 1;

  state = {
    isDragging: false,
    isHintMove: false,
  };

  componentDidMount() {
    this.makeSuperMove();
  }

  componentDidUpdate(prevProps: Props) {
    if (!this.props.superMove) return;
    if (!_isEqual(prevProps.superMove as SuperMove, this.props.superMove as SuperMove)) this.makeSuperMove();
  }

  setDragging = (isDragging: boolean, isHintMove: boolean = false) => {
    this.node.moveToTop();
    this.node.getLayer().moveToTop();
    this.setState({ isDragging, isHintMove });
  };

  onDragStart = () => {
    global.sounds.get();
    this.setDragging(true);
  };

  onDragEnd = ({ target }: any) => {
    const { x, y, dropElements, card, actions, index, size } = this.props;
    global.sounds.put();
    actions.setListening(false);
    const nodeRect = target.getClientRect({ skipShadow: true, skipStroke: true });

    const matchedDropElementsByName = dropElements.filter(dropElement => {
      return dropElement.dropList.find(item => item.name === card.name);
    });

    const matchDropElements = matchedDropElementsByName.filter(dropElement => {
      const dropElementRect = getDropElementPosition(dropElement, size);
      return haveIntersection(dropElementRect, nodeRect);
    });

    this.setDragging(true, true);

    if (!matchDropElements.length) {
      moveTo(target, x, y).then(() => {
        this.setDragging(false, false);
        actions.setListening(true);
      });
      return;
    }

    let dropElementNumber = 0;

    if (matchDropElements.length > 1) {
      const distances = [
        intersectionXDistance(nodeRect, getDropElementPosition(matchDropElements[0], size)),
        intersectionXDistance(nodeRect, getDropElementPosition(matchDropElements[1], size)),
      ];

      if (distances[1] < distances[0]) dropElementNumber = 1;
    }

    const dropElement = matchDropElements[dropElementNumber];

    const dropElementPosition = getDropElementPosition(dropElement, size);
    const cardRect = target.getClientRect({ skipShadow: true, skipStroke: true });
    const moveX = dropElementPosition.x - cardRect.x + target.x();
    const moveY = dropElementPosition.y - cardRect.y + target.y();

    global.sounds.put();

    if (dropElement.state === CardState.home) {
      global.sounds.scored();
    }

    moveTo(target, moveX, moveY).then(() => {
      this.setDragging(false);
      actions.setListening(true);

      switch (dropElement.state) {
        case CardState.slots: {
          actions.dropFromSlotsToSlots({
            slotOneNumber: index,
            slotTwoNumber: dropElement.index,
            undoPosition: { name: card.name, x, y },
          });
          break;
        }

        case CardState.laid: {
          actions.dropFromSlotsToLaid({
            slotNumber: index,
            pileNumber: dropElement.index,
            undoPosition: { name: card.name, x, y },
          });
          break;
        }

        case CardState.home: {
          actions.dropFromSlotsToHome({
            slotNumber: index,
            homeSlotNumber: dropElement.index,
            undoPosition: { name: card.name, x, y },
          });
          break;
        }
      }
    });
  };

  async moveTo(card: any, x: number, y: number) {
    this.setDragging(true, true);
    await hintMove(card, x, y);
    this.setDragging(false, false);
  }

  onDblClick = () => {
    const { dropElements, card, actions, index, size } = this.props;

    const matchedDropElements = dropElements.filter(dropElement => {
      return dropElement.state === CardState.home && dropElement.dropList.find(item => item.name === card.name);
    });

    if (!matchedDropElements.length) return;

    const dropElement = matchedDropElements[0];

    const dropElementPosition = getDropElementPosition(dropElement, size);
    const cardRect = this.node.getClientRect({ skipShadow: true, skipStroke: true });
    const x = dropElementPosition.x - cardRect.x + this.node.x();
    const y = dropElementPosition.y - cardRect.y + this.node.y();

    this.node.moveToTop();
    this.node.getLayer().moveToTop();

    global.sounds.put();
    global.sounds.scored();

    moveTo(this.node, x, y).then(() => {
      actions.dropFromSlotsToHome({
        slotNumber: index,
        homeSlotNumber: dropElement.index,
        undoPosition: { name: card.name, x: this.props.x, y: this.props.y },
      });
    });
  };

  async makeSuperMove() {
    const { superMove, actions, index, size } = this.props;
    if (!superMove) return;
    if (!(superMove.state === CardState.slots && superMove.index === index)) return;

    const stage = this.node.getStage();

    const superCard = stage.findOne(`.${superMove.name}`);

    const cardRect = superCard.getClientRect({ skipShadow: true, skipStroke: true });
    const homeY = getSlotY(size);
    const homeX = getHomeX(superMove.homeSlot, size);

    const x = homeX - cardRect.x + superCard.x();
    const y = homeY - cardRect.y + superCard.y();

    superCard.moveToTop();
    superCard.getLayer().moveToTop();

    global.sounds.put();
    global.sounds.scored();

    const name = this.node.attrs.name;

    //   {
    //     "name": "diamonds-ace",
    //     "state": "slot",
    //     "index": 2,
    //     "rate": 1,
    //     "color": "red",
    //     "putWrong": true,
    //     "pileSize": 7,
    //     "homeSlot": 1
    // }

    await moveTo(this.node, x, y, 0.1).then(() => {
      actions.dropFromSlotsToHome({
        slotNumber: superMove.index || index,
        homeSlotNumber: superMove.homeSlot,
        undoPosition: { name, x, y },
      });
    });
  }

  render() {
    const { isDragging, isHintMove } = this.state;
    const { card, x, y, cardParameters, size } = this.props;
    const { width, height, normalShadow, corner } = cardParameters;
    const shadow: number = isDragging ? (isHintMove ? normalShadow / 2 : normalShadow * 2) : normalShadow / 2;
    const gameX = getGameX(size);
    const maxXPoint = size - width - gameX;

    return (
      <Group
        ref={node => {
          this.node = node;
        }}
        name={card.name}
        moveTo={this.moveTo.bind(this)}
        x={x}
        y={y}
        draggable
        onDragStart={this.onDragStart}
        onDragEnd={this.onDragEnd}
        onClick={this.onDblClick}
        onTap={this.onDblClick}
        dragBoundFunc={function (pos: Vector2d) {
          let x = pos.x;
          if (x < gameX) x = gameX;
          else if (x > maxXPoint) x = maxXPoint;
          const y = pos.y > gameX ? pos.y : gameX;
          return {
            x,
            y,
          };
        }}
      >
        <Rect
          perfectDrawEnabled={false}
          hitStrokeWidth={0}
          shadowForStrokeEnabled={false}
          listening={false}
          fill="rgba(255,255,255,1)"
          shadowColor={'black'}
          shadowOpacity={0.5}
          shadowBlur={normalShadow}
          shadowOffset={{ x: shadow, y: shadow }}
          width={width}
          height={height}
          cornerRadius={corner}
        />
        <Card card={card} />
      </Group>
    );
  }
}

export default SlotsCard;
